import { BASE_API_URL } from '../config/constants';
import { getAccessToken } from './authService';

// Skill Categories from backend
export const SkillCategory = {
  // Professional
  Technical: 1,
  SoftSkills: 2,
  DomainKnowledge: 3,
  Tools: 4,
  Languages: 5,
  Certifications: 6,
  // Personal/Hobbies
  Sports: 7,
  Arts: 8,
  Music: 9,
  Cooking: 10,
  Gaming: 11,
  Crafts: 12,
  Outdoor: 13,
  Other: 14,
};

export const SkillCategoryNames = {
  1: 'Technical',
  2: 'Soft Skills',
  3: 'Domain Knowledge',
  4: 'Tools',
  5: 'Languages',
  6: 'Certifications',
  7: 'Sports',
  8: 'Arts',
  9: 'Music',
  10: 'Cooking',
  11: 'Gaming',
  12: 'Crafts',
  13: 'Outdoor',
  14: 'Other',
};

const getAuthHeaders = async () => {
  const token = await getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// API Functions
export const usersApi = {
  // Get current user info
  getCurrentUser: async (email) => {
    // This would need to be implemented - for now return null
    // You might need to add an endpoint to get user by email
    return null;
  },

  // Get all users by organization
  getAll: async (organizationId) => {
    const response = await fetch(`${BASE_API_URL}api/users?organizationId=${organizationId}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
  },

  // Get user by ID
  getById: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/users/${id}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user');
    return response.json();
  },

  // Create new user
  create: async (data) => {
    const response = await fetch(`${BASE_API_URL}api/users`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create user');
    }
    return response.json();
  },

  // Get user's skills
  getSkills: async (userId) => {
    const response = await fetch(`${BASE_API_URL}api/users/${userId}/skills`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user skills');
    return response.json();
  },
};

export const skillsApi = {
  // Get skills by user ID
  getUserSkills: async (userId) => {
    const response = await fetch(`${BASE_API_URL}api/Skills/user/${userId}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user skills');
    return response.json();
  },

  // Get skills by organization
  getByOrganization: async (organizationId, category = null) => {
    let url = `${BASE_API_URL}api/Skills/organization/${organizationId}`;
    if (category !== null) {
      url += `?category=${category}`;
    }
    const response = await fetch(url, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch organization skills');
    return response.json();
  },

  // Search skills
  search: async (organizationId, category = null) => {
    let url = `${BASE_API_URL}api/Skills/search?organizationId=${organizationId}`;
    if (category !== null) {
      url += `&category=${category}`;
    }
    const response = await fetch(url, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to search skills');
    return response.json();
  },

  // Create new skill
  create: async (data) => {
    const response = await fetch(`${BASE_API_URL}api/Skills`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create skill');
    return response.json();
  },

  // Update skill
  update: async (id, data) => {
    const response = await fetch(`${BASE_API_URL}api/Skills/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update skill');
    return response.json();
  },

  // Delete skill
  delete: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/Skills/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete skill');
  },

  // Endorse skill
  endorse: async (skillId, endorserId) => {
    const response = await fetch(`${BASE_API_URL}api/Skills/${skillId}/endorse`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ endorserId }),
    });
    if (!response.ok) throw new Error('Failed to endorse skill');
    return response.json();
  },

  // Remove endorsement
  removeEndorsement: async (skillId, endorserId) => {
    const response = await fetch(`${BASE_API_URL}/api/Skills/${skillId}/endorse?endorserId=${endorserId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to remove endorsement');
  },
};

// SkillMasters API - Master skills catalog
export const skillMastersApi = {
  // Get all master skills
  getAll: async (category = null) => {
    let url = `${BASE_API_URL}api/skillmasters`;
    if (category !== null) {
      url += `?category=${category}`;
    }
    const response = await fetch(url, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch master skills');
    return response.json();
  },

  // Get master skill by ID
  getById: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/skillmasters/${id}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch master skill');
    return response.json();
  },

  // Search master skills by name
  search: async (query) => {
    const response = await fetch(`${BASE_API_URL}api/skillmasters/search?query=${encodeURIComponent(query)}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to search skills');
    return response.json();
  },

  // Create new master skill
  create: async (data) => {
    const response = await fetch(`${BASE_API_URL}api/skillmasters`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create skill');
    }
    return response.json();
  },

  // Update master skill
  update: async (id, data) => {
    const response = await fetch(`${BASE_API_URL}api/skillmasters/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update skill');
    }
    return response.json();
  },

  // Delete master skill
  delete: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/skillmasters/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete skill');
    }
  },

  // Get users with this skill
  getUsersWithSkill: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/skillmasters/${id}/users`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch users with skill');
    return response.json();
  },
};

// UserSkills API - User skill assignments
export const userSkillsApi = {
  // Get all skills for a user
  getUserSkills: async (userId) => {
    const response = await fetch(`${BASE_API_URL}api/userskills/user/${userId}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user skills');
    return response.json();
  },

  // Get user skill by ID
  getById: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/userskills/${id}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user skill');
    return response.json();
  },

  // Assign skill to user
  assignSkill: async (data) => {
    const response = await fetch(`${BASE_API_URL}api/userskills`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to assign skill');
    }
    return response.json();
  },

  // Update user skill
  update: async (id, data) => {
    const response = await fetch(`${BASE_API_URL}api/userskills/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update user skill');
    return response.json();
  },

  // Remove skill from user
  delete: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/userskills/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to remove user skill');
  },

  // Endorse user skill
  endorse: async (userSkillId, endorserId, comment = null) => {
    const response = await fetch(`${BASE_API_URL}api/userskills/${userSkillId}/endorse`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ endorserId, comment }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to endorse skill');
    }
    return response.json();
  },

  // Remove endorsement
  removeEndorsement: async (userSkillId, endorsementId) => {
    const response = await fetch(`${BASE_API_URL}api/userskills/${userSkillId}/endorse/${endorsementId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to remove endorsement');
  },
};

// Activity Tasks API - Create and manage activity templates
export const activityTasksApi = {
  // Get all active activity tasks (global library)
  getAll: async () => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch activity tasks');
    return response.json();
  },

  // Get activity task by ID with required skills
  getById: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks/${id}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch activity task');
    return response.json();
  },

  // Create new activity task
  create: async (data) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create activity task');
    }
    return response.json();
  },

  // Get required skills for a task
  getRequiredSkills: async (taskId) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks/${taskId}/required-skills`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch required skills');
    return response.json();
  },

  // Add required skill to task
  addRequiredSkill: async (taskId, requiredSkill) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks/${taskId}/required-skills`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(requiredSkill),
    });
    if (!response.ok) throw new Error('Failed to add required skill');
    return response.json();
  },

  // Remove required skill from task
  removeRequiredSkill: async (requiredSkillId) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks/required-skills/${requiredSkillId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to remove required skill');
  },

  // Update activity task
  update: async (id, data) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update activity task');
    }
    return response.json();
  },

  // Delete/soft delete activity task
  delete: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/activitytasks/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete activity task');
  },
};

// Activity Assignments API - Assignment management with AI integration
export const activityAssignmentsApi = {
  // Get all assignments (for managers/admins)
  getAll: async () => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch all assignments');
    return response.json();
  },

  // Get all assignments for a specific user
  getUserAssignments: async (userId) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/user/${userId}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user assignments');
    return response.json();
  },

  // Get user assignments filtered by status
  getUserAssignmentsByStatus: async (userId, status) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/user/${userId}/status/${status}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch user assignments by status');
    return response.json();
  },

  // Get assignment by ID with full details
  getById: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/${id}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch assignment');
    return response.json();
  },

  // AI Pre-Assignment Analysis (20-40 seconds)
  analyzeUserFit: async (userId, activityTaskId) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/analyze`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ userId, activityTaskId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to analyze user fit');
    }
    return response.json();
  },

  // Create new assignment (typically after AI analysis)
  create: async (data) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create assignment');
    }
    return response.json();
  },

  // Update assignment status
  updateStatus: async (id, status) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/${id}/status`, {
      method: 'PATCH',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ status }),
    });
    if (!response.ok) throw new Error('Failed to update assignment status');
    return response.json();
  },

  // AI Personalized Guidance (15-30 seconds)
  getPersonalizedGuidance: async (assignmentId) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/${assignmentId}/guidance`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate personalized guidance');
    }
    return response.json();
  },

  // Update assignment details
  update: async (id, data) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update assignment');
    return response.json();
  },

  // Delete assignment
  delete: async (id) => {
    const response = await fetch(`${BASE_API_URL}api/activityassignments/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete assignment');
  },
};

export const activitiesApi = {
  // This would need to be implemented if you have an activities endpoint
  // For now, returning empty array
  getUserActivities: async (userId) => {
    // TODO: Implement activities endpoint
    return [];
  },
};

// Career Discovery API
export const careerApi = {
  // Generate career roadmap
  discoverCareer: async (careerGoal, userId) => {
    const response = await fetch(`${BASE_API_URL}api/career/discover`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        careerGoal,
        userId
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.message || 'Failed to generate career roadmap');
    }

    return response.json();
  },
};

// CV Management API
export const cvApi = {
  // Upload a CV file (optionally for a specific user)
  upload: async (file, targetUserId = null) => {
    const token = await getAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    if (targetUserId) {
      formData.append('targetUserId', targetUserId);
    }

    const response = await fetch(`${BASE_API_URL}api/cv/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.message || 'Failed to upload CV');
    }

    return response.json();
  },

  // Get all CVs for current user
  getMyCvs: async () => {
    const response = await fetch(`${BASE_API_URL}api/cv/my-cvs`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch CVs');
    return response.json();
  },

  // Get specific CV by ID
  getById: async (cvId) => {
    const response = await fetch(`${BASE_API_URL}api/cv/${cvId}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch CV');
    return response.json();
  },

  // Download CV file
  download: async (cvId) => {
    const token = await getAccessToken();
    const response = await fetch(`${BASE_API_URL}api/cv/${cvId}/download`, {
      credentials: 'include',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    if (!response.ok) throw new Error('Failed to download CV');
    return response.blob();
  },

  // Get secure temporary URL for CV
  getUrl: async (cvId, expiryMinutes = 60) => {
    const response = await fetch(`${BASE_API_URL}api/cv/${cvId}/url?expiryMinutes=${expiryMinutes}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to get CV URL');
    return response.json();
  },

  // Delete CV
  delete: async (cvId) => {
    const response = await fetch(`${BASE_API_URL}api/cv/${cvId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete CV');
    return response.json();
  },

  // Get CVs for a specific user (admin function)
  getUserCvs: async (userId) => {
    const response = await fetch(`${BASE_API_URL}api/cv/user/${userId}`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      // If endpoint doesn't exist yet, return empty array
      if (response.status === 404) return [];
      throw new Error('Failed to fetch user CVs');
    }
    return response.json();
  },
};
